import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
// Import from the service layer, not from a UI component file.
// MemberVaccination.jsx re-exports this for backward compatibility, but
// direct service imports are the correct pattern going forward.
import { createVaccinationRecord } from "./VaccinationModule/services/vaccinationApiService";
import { uwinAPI } from "../../../services/uwin.api";
import { getBeneficiaryAgeDisplay } from "../../../helpers/common-uwinhelper";

const VaccinationRejectionCard = ({ 
  toggleDrawer, 
  beneficiaryData, 
  vaccinationData, 
  onBack, 
  onRejectSuccess
}) => {
  const [selectedRejectionReason, setSelectedRejectionReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false); // Track if user has interacted with dropdown
  const [customReasonTouched, setCustomReasonTouched] = useState(false);
  
  // API state management - no static fallback
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [isLoadingReasons, setIsLoadingReasons] = useState(true); // Start with loading true
  const [reasonsError, setReasonsError] = useState(null);

  // Fetch rejection reasons from API only
  useEffect(() => {
    const fetchRejectionReasons = async () => {
      try {
        setIsLoadingReasons(true);
        setReasonsError(null);
        
        // Call the API
        const response = await uwinAPI.vaccinationNotDoneReasons();
        
        console.log('Rejection Reasons API Response:', response);
        
        // Extract and sort the data array from the response
        if (response.success && response.data && Array.isArray(response.data)) {
          // Sort reasons by reason_order if available
          const sortedReasons = response.data.sort((a, b) => {
            const orderA = a.reason_order || 999;
            const orderB = b.reason_order || 999;
            return orderA - orderB;
          });
          setRejectionReasons(sortedReasons);
        } else {
          console.warn('API response structure unexpected:', response);
          setReasonsError('Invalid response format from server');
        }
        
      } catch (error) {
        console.error('Error fetching rejection reasons:', error);
        const errorMessage = error.message || 'Failed to fetch rejection reasons';
        setReasonsError(errorMessage);
        toast.error('Failed to load rejection reasons from server');
        
      } finally {
        setIsLoadingReasons(false);
      }
    };

    // Fetch reasons when component mounts
    fetchRejectionReasons();
  }, []);

  // Retry function for failed API calls
  const retryFetchReasons = () => {
    setReasonsError(null);
    setIsLoadingReasons(true);
    
    // Re-trigger useEffect logic
    const fetchRejectionReasons = async () => {
      try {
        const response = await uwinAPI.vaccinationNotDoneReasons();
        
        if (response.success && response.data && Array.isArray(response.data)) {
          const sortedReasons = response.data.sort((a, b) => {
            const orderA = a.reason_order || 999;
            const orderB = b.reason_order || 999;
            return orderA - orderB;
          });
          setRejectionReasons(sortedReasons);
        } else {
          setReasonsError('Invalid response format from server');
        }
        
      } catch (error) {
        const errorMessage = error.message || 'Failed to fetch rejection reasons';
        setReasonsError(errorMessage);
        toast.error('Failed to load rejection reasons from server');
        
      } finally {
        setIsLoadingReasons(false);
      }
    };

    fetchRejectionReasons();
  };

  // Handle reject vaccination submission
  const handleRejectVaccination = async () => {
    try {
      // Validate that reasons are loaded
      if (reasonsError) {
        toast.error("Cannot submit - failed to load rejection reasons");
        return;
      }

      if (isLoadingReasons) {
        toast.error("Please wait for rejection reasons to load");
        return;
      }

      // Validate that a reason is selected
      if (!selectedRejectionReason) {
        toast.error("Please select a reason for rejection");
        return;
      }

      if (selectedRejectionReason === "VNDR_003" && !customReason.trim()) {
        toast.error("Please enter a reason");
        return;
      }

      // Validate that consent is checked
      if (!consentChecked) {
        toast.error("Please confirm that you understand the beneficiary would not be able to take the vaccine in this session");
        return;
      }

      setIsSubmitting(true);

      console.log('Rejection Card - Starting rejection with data:', { selectedRejectionReason, vaccinationData });

      // Find the selected reason object from API data
      const selectedReasonObj = rejectionReasons.find(reason => reason.reasonId === selectedRejectionReason);
      const reasonName = selectedReasonObj?.reasonName || "Unknown Reason";

      // Prepare rejection data for centralized API
      const rejectionData = {
        reasonId: selectedRejectionReason,
        reasonName: reasonName,
        customReason: selectedRejectionReason === "VNDR_003" ? customReason.trim() : "",
        selectedVaccines: vaccinationData?.selectedVaccines || [],
        consentGiven: consentChecked
      };

      // Call the centralized API service with rejection type
      const result = await createVaccinationRecord(
        vaccinationData || {},           // Form data from Step 1 (may be empty for rejection)
        'rejection',                     // Rejection type
        rejectionData,                   // Rejection specific data
        beneficiaryData,                 // Beneficiary info
        vaccinationData?.villages || [], // Village data
        vaccinationData?.healthVillages || [], // Health village data
        vaccinationData?.wards || [],    // Ward data
        vaccinationData?.healthWards || [] // Health ward data for name lookup
      );

      console.log('Rejection API result:', result);

      if (result.success) {
        toast.success(result.message || "Vaccination rejected successfully");
        
        // Reset form state
        setSelectedRejectionReason("");
        setCustomReason("");
        setConsentChecked(false);
        setReasonTouched(false); // Reset touched state
        setCustomReasonTouched(false);
        
        // Prepare rejection completion data
        const rejectionCompletionData = {
          reasonName,
          reasonId: selectedRejectionReason,
          apiResult: result.data,
          timestamp: new Date().toISOString(),
          ...result.payload
        };

        // Notify parent component
        if (onRejectSuccess) {
          onRejectSuccess(rejectionCompletionData);
        } else {
          // Default: close drawer
          toggleDrawer(false)();
        }
      } else {
        toast.error(result.message || "Failed to reject vaccination");
      }
      
    } catch (error) {
      console.error("Error rejecting vaccination:", error);
      
      // Handle specific error messages
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error?.message) {
        toast.error(error.message);
      } else {
        toast.error("Failed to reject vaccination");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle reset/cancel
  const handleCancel = () => {
    setSelectedRejectionReason("");
    setCustomReason("");
    setConsentChecked(false);
    setReasonTouched(false); 
    setCustomReasonTouched(false); 
    
    if (onBack) {
      onBack();
    } else {
      toggleDrawer(false)();
    }
  };

  return (
    <Box sx={{ width: 490, height: "100vh", bgcolor: "white" }}>
      {/* ================= HEADER ================= */}
      <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Back button */}
            {onBack && (
              <IconButton onClick={onBack} size="small" sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <WarningIcon sx={{ color: "error.main" }} />
            <Typography variant="h6" fontWeight="bold" color="error.main">
              Reject Vaccination
            </Typography>
          </Box>
          
          <IconButton 
            onClick={() => onRejectSuccess ? onRejectSuccess() : toggleDrawer(false)()} 
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* ================= REJECTION CONTENT ================= */}
      <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
        
        {/* Member Details Accordion */}
        <Card sx={{ mb: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}>
          <Accordion defaultExpanded={false}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "#f5f5f5",
                minHeight: "auto",
                "& .MuiAccordionSummary-content": {
                  margin: "12px 0",
                  "&.Mui-expanded": { margin: "12px 0" }
                }
              }}
            >
              <Box sx={{ width: "100%" }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  MEMBER DETAILS
                </Typography>
                <Box>
                  <Typography variant="body2" component="span" fontWeight={500}>
                    {beneficiaryData?.ben_name || "N/A"}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    component="span" 
                    color="text.secondary" 
                    sx={{ ml: 1 }}
                  >
                    {getBeneficiaryAgeDisplay(beneficiaryData)} / {beneficiaryData?.beneficiary_gender || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Date of Birth
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {beneficiaryData?.dob || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Card>

        {/* Loading State for Reasons */}
        {isLoadingReasons && !reasonsError && (
          <Card sx={{ mb: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={24} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading rejection reasons...
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Rejection Reason Section - Only show when not loading and no error */}
        {!isLoadingReasons && !reasonsError && (
        <Card sx={{ mb: 3, borderRadius: 2, border: "2px solid #f44336" }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" color="error.main" sx={{ mb: 2 }}>
              Rejection Details
            </Typography>
            
            {/* Reason Dropdown */}
            <Box sx={{ mb: 2 }}>
              <Typography fontSize={14} mb={1} color="text.primary">
                Choose reason for rejection *
              </Typography>
              <TextField 
                select 
                fullWidth 
                size="small"
                value={selectedRejectionReason}
                onChange={(e) => setSelectedRejectionReason(e.target.value)}
                onFocus={() => setReasonTouched(true)} // Mark as touched when user focuses
                error={reasonTouched && !selectedRejectionReason} // Only show error if touched and empty
                helperText={reasonTouched && !selectedRejectionReason ? "Please select a reason" : ""}
                disabled={rejectionReasons.length === 0}
                sx={{
                  '& .MuiSelect-select': {
                    padding: '8px 14px',
                    minHeight: 'auto',
                    backgroundColor: 'white'
                  }
                }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  {rejectionReasons.length === 0 
                    ? "No rejection reasons available" 
                    : "Select reason for rejection"
                  }
                </MenuItem>
                {rejectionReasons.map((reason) => (
                  <MenuItem key={reason.reasonId} value={reason.reasonId}>
                    {reason.reasonName}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Custom Reason Field */}
            {selectedRejectionReason === "VNDR_003" && (
              <Box sx={{ mb: 2 }}>
                <Typography fontSize={14} mb={1} color="text.primary">
                  Specify reason *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  onBlur={() => setCustomReasonTouched(true)}
                  placeholder="Enter your reason"
                  error={customReasonTouched && !customReason.trim()}
                  helperText={customReasonTouched && !customReason.trim() ? "Please specify a reason" : ""}
                />
              </Box>
            )}

            {/* Consent Checkbox */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Checkbox
                size="small"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                disabled={rejectionReasons.length === 0}
                sx={{ 
                  color: 'error.main',
                  '&.Mui-checked': { color: 'error.main' },
                  mt: -0.5,
                }}
              />
              <Typography 
                fontSize={13} 
                color="text.primary"
                sx={{ lineHeight: 1.3, mt: 0.5 }}
              >
                I understand that the beneficiary would not be able to take the vaccine in this session <Typography component="span" color="error">*</Typography>
              </Typography>
            </Box>
          </CardContent>
        </Card>
        )}

        {/* Action Buttons */}
        <Box sx={{ mt: "auto", pt: 2 }}>
          {/* Confirm Rejection Button */}
          <Button
            variant="contained"
            color="error"
            fullWidth
            disabled={
              isSubmitting || 
              isLoadingReasons || 
              !!reasonsError || 
              rejectionReasons.length === 0 ||
              !selectedRejectionReason || 
              !consentChecked
            }
            onClick={handleRejectVaccination}
            sx={{
              py: 1.5,
              mb: 2,
              borderRadius: 3,
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
              "&:hover": {
                boxShadow: "0 6px 16px rgba(244, 67, 54, 0.4)",
              },
              "&:disabled": {
                backgroundColor: "grey.300",
                color: "grey.500",
              }
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Processing Rejection...
              </Box>
            ) : (
              "Confirm Rejection"
            )}
          </Button>

          {/* Cancel Button */}
          <Button
            variant="text"
            fullWidth
            onClick={handleCancel}
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              borderRadius: 3,
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 600,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "grey.100",
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default VaccinationRejectionCard;